import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from '@tanstack/react-router';
import { Info } from 'lucide-react';
import { debugZodResolver } from '@/lib/debugZodResolver';
import TourConfigBreadcrumbs from '@/features/dashboard/tourConfig/components/TourConfigBreadcrumbs.tsx';
import TourConfigHeader from '@/features/dashboard/tourConfig/components/TourConfigHeader.tsx';
import { tourTransportConfigRoute } from '@/features/dashboard/tourTransport/routes/tourTransportConfigRoute.ts';
import {
  TransportFormData,
  TransportInputForm,
  transportSchema,
  transportDefaultValues,
} from '@/features/dashboard/tourTransport/schemas/transportSchema.ts';
import useGetTransport from '@/features/dashboard/tourTransport/hooks/useGetTransport.ts';
import usePutTourTransportAndTfg from '@/features/dashboard/tourTransport/hooks/usePutTourTransportAndTfg.ts';
import { transportConfigurationMapper } from '@/features/dashboard/tourTransport/types/TransportConfigurationDto.mapper.ts';
import useGetTfgSettings from '@/features/dashboard/tfg/hooks/useGetTfgSettings.ts';
import { tfgSettingsMapper } from '@/features/dashboard/tfg/types/TfgSettingsDto.mapper.ts';
import TourTransportConfigCard from '@/features/dashboard/tourTransport/components/TourTransportConfigCard.tsx';
import TourTransportConfigPageSkeleton from '@/features/dashboard/tourTransport/components/TourTransportConfigPageSkeleton.tsx';

const TourTransportConfigPage = () => {
  const navigate = useNavigate();
  const { tourId } = tourTransportConfigRoute.useParams();

  const transportForm = useForm<TransportInputForm>({
    resolver: debugZodResolver(transportSchema),
    mode: 'onChange',
    defaultValues: transportDefaultValues,
  });

  const { mutate, isPending } = usePutTourTransportAndTfg(tourId);
  const { data: transport, isPending: transportLoading } =
    useGetTransport(tourId);
  const { data: tfgSettings, isPending: tfgLoading } =
    useGetTfgSettings(tourId);

  const isLoading = transportLoading || tfgLoading;
  const locked = (transport?.locked ?? false) || (tfgSettings?.locked ?? false);

  useEffect(() => {
    if (isLoading) return;
    if (transport) {
      const base = transportConfigurationMapper.fromDtoToForm(transport);
      transportForm.reset(
        tfgSettings ? tfgSettingsMapper.applyToForm(base, tfgSettings) : base,
      );
    }
  }, [transport, tfgSettings, isLoading, transportForm]);

  const onSubmit = (values: TransportFormData) => {
    mutate(values, {
      onSuccess: () => {
        navigate({ to: `/app/tours/${tourId}/config/options` });
      },
    });
  };

  const onCancel = () => {
    navigate({ to: `/app/tours/${tourId}/config/costs` });
  };

  if (isLoading) {
    return <TourTransportConfigPageSkeleton />;
  }

  return (
    <div className="space-y-4">
      <TourConfigBreadcrumbs />
      <FormProvider {...transportForm}>
        <form
          className="flex-1 space-y-4"
          onSubmit={transportForm.handleSubmit(onSubmit)}
        >
          <TourConfigHeader
            title="Transport"
            isLoading={isPending}
            submit={transportForm.handleSubmit(onSubmit)}
            onCancel={onCancel}
            showSaveButton={!locked}
          />
          <div className="mx-auto max-w-[600px] space-y-2">
            {locked && (
              <div className="flex items-start gap-2 rounded-md border border-neutral-200 bg-neutral-50 p-3 text-sm text-base-text">
                <Info className="mt-0.5 size-4 shrink-0 text-neutral-400" />
                <span>
                  Konfiguracja transportu jest zablokowana, ponieważ istnieje
                  już rezerwacja na ten wyjazd. Ustawienia można tylko
                  przeglądać.
                </span>
              </div>
            )}
            <fieldset disabled={locked} className="space-y-2">
              <TourTransportConfigCard />
            </fieldset>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default TourTransportConfigPage;
